import { FC } from "react";
import { useSelector } from "react-redux";
import "./eventTypeSelector.scss";
import { SubscribedEventType } from "utils/enums";
import BaseCheckbox from "components/baseCheckbox/baseCheckbox";
import { useTranslation } from "react-i18next";
import { StringResources } from "utils/language/languageResource";
import BaseBanner from "components/baseBanner/baseBanner";
import { RootState } from "redux/store";

interface EventOption {
    value: number;
    label: string;
    group: "payment" | "tokenization";
}

interface EventTypeSelectorProps {
    selectedEventTypes: number[];
    onChange: (selectedEventTypes: number[]) => void;
    error?: string;
}

const EventTypeSelector: FC<EventTypeSelectorProps> = ({ selectedEventTypes, onChange, error }) => {
    const { t } = useTranslation();

    const eventGroups = [
        { id: "payment", label: StringResources.eventTypeSelector.groups.payment },
        // { id: "tokenization", label: StringResources.eventTypeSelector.groups.tokenization },
    ];

    // Hardcoded event types instead of filtering from filterTypes.webhookEventTypes.
    // Reason: Me and Neven agreed that we don't want to automatically show new event types that backend might add
    // during testing/production without frontend coordination. This gives us explicit control over what gets displayed in the UI.
    const eventTypes: EventOption[] = [
        {
            value: SubscribedEventType.Authorization,
            label: t(StringResources.eventTypeSelector.types.authorization),
            group: "payment",
        },
        {
            value: SubscribedEventType.Capture,
            label: t(StringResources.eventTypeSelector.types.capture),
            group: "payment",
        },
        {
            value: SubscribedEventType.Adjustment,
            label: t(StringResources.eventTypeSelector.types.adjustment),
            group: "payment",
        },
        {
            value: SubscribedEventType.Refund,
            label: t(StringResources.eventTypeSelector.types.refund),
            group: "payment",
        },
        {
            value: SubscribedEventType.Tokenization,
            label: t(StringResources.eventTypeSelector.types.tokenization),
            group: "payment",
        },
        // {
        //     value: SubscribedEventType.TokenDeleted,
        //     label: t(StringResources.eventTypeSelector.types.tokenDeleted),
        //     group: "tokenization",
        // },
    ];

    const handleEventTypeChange = (id: string, checked: boolean) => {
        const eventTypeId = parseInt(id);

        if (checked) {
            if (!selectedEventTypes.includes(eventTypeId)) {
                onChange([...selectedEventTypes, eventTypeId]);
            }
        } else {
            onChange(selectedEventTypes.filter(type => type !== eventTypeId));
        }
    };

    return (
        <div className="event-type-selector">
            <div className="event-type-selector__header">
                <span>{t(StringResources.eventTypeSelector.title).toString()}</span>
            </div>

            <BaseBanner
                text={t(StringResources.eventTypeSelector.infoBanner).toString()}
                actions={[
                    {
                        label: t(StringResources.eventTypeSelector.openDocumentation).toString(),
                        onClick: () => window.open("https://docs.straumur.is/category/webhooks", "_blank"),
                    },
                ]}
            />

            <div className="event-type-selector__content">
                <div className="event-type-selector__table">
                    <div className="event-type-selector__table-header">
                        <div className="event-type-selector__column">
                            {t(StringResources.eventTypeSelector.eventGroup).toString()}
                        </div>
                        <div className="event-type-selector__column">
                            {t(StringResources.eventTypeSelector.eventType).toString()}
                        </div>
                    </div>

                    {eventGroups.map(group => (
                        <div key={group.id} className="event-type-selector__row">
                            <div className="event-type-selector__column event-type-selector__group-column">
                                {t(group.label).toString()}
                            </div>
                            <div className="event-type-selector__column">
                                <div className="event-type-selector__type-options">
                                    {eventTypes
                                        .filter(et => et.group === group.id)
                                        .map(eventType => (
                                            <div key={eventType.value} className="event-type-selector__type-option">
                                                <BaseCheckbox
                                                    id={eventType.value.toString()}
                                                    checked={selectedEventTypes.includes(eventType.value)}
                                                    onChange={handleEventTypeChange}
                                                    label={eventType.label}
                                                />
                                            </div>
                                        ))}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {error && (
                <div className="event-type-selector__error">
                    <span className="error-text">{error}</span>
                </div>
            )}
        </div>
    );
};

export default EventTypeSelector;
